import { z } from 'zod';
import { uuidSchema } from './common.schema.js';

// hospital_id is resolved by the controller from the caller's decrypted token
// (getScopeId), not a client-supplied body/query field.

// ── Readiness Areas ──────────────────────────────────────────────────────────

export const ReadinessAreaStatusSchema = z.enum(['ready', 'needs_review']);

export const CreateReadinessAreaSchema = z.object({
  area:           z.string().min(1).max(100),
  score:          z.number().int().min(0).max(100).optional(),
  last_tested_at: z.coerce.date().optional(),
  status:         ReadinessAreaStatusSchema.optional(),
});
export type CreateReadinessAreaRequest = z.infer<typeof CreateReadinessAreaSchema>;

export const UpdateReadinessAreaSchema = z.object({
  area:           z.string().min(1).max(100).optional(),
  score:          z.number().int().min(0).max(100).optional(),
  last_tested_at: z.coerce.date().nullable().optional(),
  status:         ReadinessAreaStatusSchema.optional(),
});
export type UpdateReadinessAreaRequest = z.infer<typeof UpdateReadinessAreaSchema>;

export const ReadinessAreaResponseSchema = z.object({
  id:             uuidSchema,
  hospital_id:    uuidSchema,
  area:           z.string(),
  score:          z.number(),
  last_tested_at: z.string().nullable(),
  status:         ReadinessAreaStatusSchema,
  created_at:     z.string(),
  updated_at:     z.string(),
});
export type ReadinessAreaResponse = z.infer<typeof ReadinessAreaResponseSchema>;

export const ReadinessAreaListQuerySchema = z.object({});

// ── Emergency Contacts ───────────────────────────────────────────────────────

export const CreateEmergencyContactSchema = z.object({
  role:        z.string().min(1).max(100),
  name:        z.string().min(1).max(150),
  available:   z.boolean().optional(),
});
export type CreateEmergencyContactRequest = z.infer<typeof CreateEmergencyContactSchema>;

export const UpdateEmergencyContactSchema = z.object({
  role:      z.string().min(1).max(100).optional(),
  name:      z.string().min(1).max(150).optional(),
  available: z.boolean().optional(),
});
export type UpdateEmergencyContactRequest = z.infer<typeof UpdateEmergencyContactSchema>;

export const EmergencyContactResponseSchema = z.object({
  id:          uuidSchema,
  hospital_id: uuidSchema,
  role:        z.string(),
  name:        z.string(),
  available:   z.boolean(),
  created_at:  z.string(),
  updated_at:  z.string(),
});
export type EmergencyContactResponse = z.infer<typeof EmergencyContactResponseSchema>;

export const EmergencyContactListQuerySchema = z.object({});

// ── Emergency Equipment ──────────────────────────────────────────────────────

export const EmergencyEquipmentStatusSchema = z.enum(['operational', 'needs_attention', 'out_of_service']);

export const CreateEmergencyEquipmentSchema = z.object({
  item:          z.string().min(1).max(100),
  status:        EmergencyEquipmentStatusSchema.optional(),
  last_check_at: z.coerce.date().optional(),
  next_check_at: z.coerce.date().optional(),
});
export type CreateEmergencyEquipmentRequest = z.infer<typeof CreateEmergencyEquipmentSchema>;

export const UpdateEmergencyEquipmentSchema = z.object({
  item:          z.string().min(1).max(100).optional(),
  status:        EmergencyEquipmentStatusSchema.optional(),
  last_check_at: z.coerce.date().nullable().optional(),
  next_check_at: z.coerce.date().nullable().optional(),
});
export type UpdateEmergencyEquipmentRequest = z.infer<typeof UpdateEmergencyEquipmentSchema>;

export const EmergencyEquipmentResponseSchema = z.object({
  id:            uuidSchema,
  hospital_id:   uuidSchema,
  item:          z.string(),
  status:        EmergencyEquipmentStatusSchema,
  last_check_at: z.string().nullable(),
  next_check_at: z.string().nullable(),
  created_at:    z.string(),
  updated_at:    z.string(),
});
export type EmergencyEquipmentResponse = z.infer<typeof EmergencyEquipmentResponseSchema>;

export const EmergencyEquipmentListQuerySchema = z.object({});

// ── Emergency Drills ────────

export const EmergencyDrillStatusSchema = z.enum(['completed', 'planned']);

export const CreateEmergencyDrillSchema = z.object({
  drill_type:       z.string().min(1).max(100),
  drill_date:       z.coerce.date(),
  duration_minutes: z.number().int().min(0).optional(),
  participants:     z.number().int().min(0).optional(),
  outcome:          z.string().max(50).optional(),
  status:           EmergencyDrillStatusSchema.optional(),
});
export type CreateEmergencyDrillRequest = z.infer<typeof CreateEmergencyDrillSchema>;

export const UpdateEmergencyDrillSchema = z.object({
  drill_type:       z.string().min(1).max(100).optional(),
  drill_date:       z.coerce.date().optional(),
  duration_minutes: z.number().int().min(0).nullable().optional(),
  participants:     z.number().int().min(0).nullable().optional(),
  outcome:          z.string().max(50).nullable().optional(),
  status:           EmergencyDrillStatusSchema.optional(),
});
export type UpdateEmergencyDrillRequest = z.infer<typeof UpdateEmergencyDrillSchema>;

export const EmergencyDrillResponseSchema = z.object({
  id:               uuidSchema,
  hospital_id:      uuidSchema,
  drill_type:       z.string(),
  drill_date:       z.string(),
  duration_minutes: z.number().nullable(),
  participants:     z.number().nullable(),
  outcome:          z.string().nullable(),
  status:           EmergencyDrillStatusSchema,
  created_at:       z.string(),
});
export type EmergencyDrillResponse = z.infer<typeof EmergencyDrillResponseSchema>;

export const EmergencyDrillListQuerySchema = z.object({});

// ── Emergency Preparedness Summary (singleton per hospital) ──────────────────

export const UpdateEmergencyPreparednessSummarySchema = z.object({
  staff_trained_percentage: z.number().int().min(0).max(100).optional(),
  drills_planned:           z.number().int().min(0).optional(),
});
export type UpdateEmergencyPreparednessSummaryRequest = z.infer<typeof UpdateEmergencyPreparednessSummarySchema>;

export const EmergencyPreparednessSummaryResponseSchema = z.object({
  hospital_id:              uuidSchema,
  staff_trained_percentage: z.number(),
  drills_planned:           z.number(),
  updated_at:               z.string(),
});
export type EmergencyPreparednessSummaryResponse = z.infer<typeof EmergencyPreparednessSummaryResponseSchema>;

export const EmergencyPreparednessSummaryQuerySchema = z.object({});
